import React, { useCallback, useEffect, useRef } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { TRACKING_TRUCK_MOTION } from '../orderTracking.constants';
import {
	TruckMotionController,
	TruckMotionFrame,
	TruckMotionTarget,
} from '../truckMotion.controller';

interface TruckMotionProps {
	markers: React.MutableRefObject<Map<number, L.Marker>>;
	targets: TruckMotionTarget[];
	enabled?: boolean;
	/** every batch of frames, also the last ones a snap or a zoom ends with */
	onFrames?: (frames: TruckMotionFrame[]) => void;
}

const TruckMotion: React.FC<TruckMotionProps> = ({
	markers,
	targets,
	enabled = TRACKING_TRUCK_MOTION.enabled,
	onFrames,
}) => {
	const map = useMap();
	const controller = useRef<TruckMotionController>();
	if (!controller.current) {
		controller.current = new TruckMotionController({
			...TRACKING_TRUCK_MOTION,
			enabled,
		});
	}
	const motion = controller.current;
	const frame = useRef(0);
	const zooming = useRef(false);
	// Read at call time: a new callback must not restart the slide loop.
	const onFramesRef = useRef(onFrames);
	onFramesRef.current = onFrames;
	const emit = useCallback((frames: TruckMotionFrame[]) => {
		if (frames.length) onFramesRef.current?.(frames);
	}, []);

	const draw = useCallback(
		({ id, position, target }: TruckMotionFrame) => {
			const marker = markers.current.get(id);
			const element = marker?.getElement();
			if (!marker || !element) return;
			// Only a marker Leaflet holds on its fix is drawn along the slide:
			// one fanned out of a cluster sits away from it on purpose.
			const held = marker.getLatLng();
			if (held.lat !== target.lat || held.lng !== target.lng) return;
			const point = map.latLngToLayerPoint(position).round();
			L.DomUtil.setPosition(element, point);
			// as Leaflet stacks markers: the lower on screen, the higher
			element.style.zIndex = String(
				point.y + (marker.options.zIndexOffset ?? 0),
			);
		},
		[map, markers],
	);

	const run = useCallback(() => {
		cancelAnimationFrame(frame.current);
		frame.current = 0;
		if (zooming.current) return;
		const frames = motion.tick(performance.now());
		frames.forEach(draw);
		emit(frames);
		if (motion.inFlight()) {
			frame.current = requestAnimationFrame(run);
		}
	}, [motion, draw, emit]);

	useEffect(() => {
		const frames = motion.setEnabled(enabled);
		frames.forEach(draw);
		emit(frames);
	}, [motion, enabled, draw, emit]);

	useEffect(() => {
		const now = performance.now();
		motion.keepOnly(targets.map((target) => target.id));
		targets.forEach((target) => motion.setTarget(target, now));
		// A zoom draws every marker from its fix itself; no slide meanwhile.
		if (zooming.current) emit(motion.snapAll());
		// react-leaflet has just put the markers on their new fixes; draw them
		// back at the start of their slide before this frame paints.
		run();
	}, [motion, targets, run, emit]);

	useEffect(() => {
		const onZoomStart = () => {
			zooming.current = true;
			// The markers are left to Leaflet; the lines get their final shape.
			emit(motion.snapAll());
		};
		const onZoomEnd = () => {
			zooming.current = false;
		};
		map.on('zoomstart', onZoomStart);
		map.on('zoomend', onZoomEnd);
		return () => {
			map.off('zoomstart', onZoomStart);
			map.off('zoomend', onZoomEnd);
			cancelAnimationFrame(frame.current);
		};
	}, [map, motion, emit]);

	return null;
};

export default TruckMotion;
